import { Button, Card, Checkbox, Label } from '@features/ui';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCheckoutStore } from '@stores/checkoutStore.ts';
import { getPublicTerms, TermsRes } from '@src/services/termsAndConditions';

type Props = {
  onBack: () => void;
  onConfirm: () => void;
};

const TermCard = ({ children }: { children: React.ReactNode }) => {
  return (
    <Card className="bg-card-500 flex flex-col gap-y-4 mb-2 p-3 relative pr-4 lg:pr-12">
      <div className="absolute top-5 right-2.5 w-[7px] h-[45px] bg-primary rounded-2xl" />
      {children}
    </Card>
  );
};

export const TermAndCondition: React.FC<Props> = ({ onBack, onConfirm }) => {
  const { t } = useTranslation();
  const { payment, setPayment } = useCheckoutStore();
  const [terms, setTerms] = useState<TermsRes[]>([]);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = React.useState<Record<number, boolean>>({});

  useEffect(() => {
    let mounted = true;

    const loadTerms = async () => {
      try {
        const res = await getPublicTerms();

        if (mounted && res.data) {
          const flattenedTerms = res.data.flatMap((group) => group.terms);

          setTerms(flattenedTerms);
          // Initialize checked state for all terms - always start unchecked
          const initialChecked: Record<number, boolean> = {};

          flattenedTerms.forEach((term) => {
            initialChecked[term.id] = false;
          });
          setChecked(initialChecked);
        }
      } catch (error) {
        console.error('Failed to load terms:', error);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadTerms();

    return () => {
      mounted = false;
    };
  }, []);

  const allAgreed = Object.values(checked).every(Boolean);
  // Prepare accepted terms array for API
  // type is now a ContentTypeItem object – send its key string for the AcceptedTerm contract
  const acceptedTerms = terms
    .filter((term) => checked[term.id])
    .map((term) => ({ content: term.content, type: term.type?.key ?? '' }));

  const handleCheckboxChange = (key: number, value: boolean) => {
    setChecked((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <Card className="max-w-6xl m-auto pt-2 px-4 lg:pt-4 lg:px-8 mt-4">
      <h3 className="text-2xl text-center pb-2 lg:pb-5">
        {t('term.terms&Conditions')}
      </h3>
      <Card className="bg-card-300 px-5 py-3 lg:px-8 lg:py-6">
        {loading ? (
          <div className="text-center py-8">
            <p className="text-sm">Loading terms...</p>
          </div>
        ) : terms.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-sm">No terms available.</p>
          </div>
        ) : (
          <>
            {terms.map((term) => (
              <div key={term.id} className="mb-5">
                <TermCard>
                  <p className="text-sm font-bold uppercase">{term.name}</p>
                  <div
                    dangerouslySetInnerHTML={{ __html: term.content }}
                    className={
                      'text-sm leading-6 whitespace-normal [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:my-3 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:my-3 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:my-2 [&_p]:my-2 [&_strong]:font-semibold [&_a]:underline [&_a]:text-primary [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:my-2 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:my-2 [&_li]:my-1 [&_li::marker]:text-white'
                    }
                  />
                </TermCard>
                <div className="flex items-center gap-x-2 ml-[21px] mt-[10px]">
                  <Checkbox
                    checked={!!checked[term.id]}
                    className="size-[20px]"
                    id={term.id.toString()}
                    onCheckedChange={(value) => {
                      handleCheckboxChange(term.id, value === true);
                    }}
                  />
                  <Label
                    className="text-sm font-bold"
                    htmlFor={term.id.toString()}
                  >
                    {term.type?.key === 'no_refund_policy'
                      ? 'I agree to be legally bound and acknowledge that there are NO refunds. By checking this box, I agree to be Legally Bound by the No Refund Policy.'
                      : 'I have read and agree to the'}{' '}
                    {term.name}
                  </Label>
                </div>
              </div>
            ))}
          </>
        )}
        <hr className="border-white " />
        <div className="flex pt-[40px] justify-between items-center gap-x-4">
          <Button
            className="lg:min-w-[145px]"
            size={'lg'}
            variant={'outline'}
            onClick={onBack}
          >
            Previous: Add-ons
          </Button>
          <Button
            className="lg:min-w-[145px]"
            disabled={!allAgreed || loading}
            size={'lg'}
            variant={'gradient'}
            onClick={() => {
              setPayment({
                ...(payment || {}),
                terms_accepted: acceptedTerms,
              });
              onConfirm();
            }}
          >
            {t('common.form.proceed')}
          </Button>
        </div>
      </Card>
    </Card>
  );
};
